import { useEffect, useState } from "react";
import api from "../../lib/api";
import {
  INCIDENT_SEVERITY_LABELS, INCIDENT_STATE_LABELS, POSTMORTEM_FIELDS, fromLocalInput, incidentFormProblem, incidentMoveLabel, toLocalInput,
} from "../../lib/product";
import { Footer, OwnerSelect, useRequest, usePeople } from "./ProductDialogs";
import { ItemPicker, TicketPicker } from "./ProductPickers";
import { SelectField } from "../tickets/TicketDialogs";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";

export const INCIDENT_PEOPLE = "/product/incidents/people";

// --- Edit the fiche (not the state): title, severity, texts, responsible, times ----------------------------------------------------
export function IncidentEditDialog({ incident, open, onOpenChange, onDone }) {
  const req = useRequest();
  const people = usePeople(open, INCIDENT_PEOPLE);
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    if (open && incident) {
      setDraft({ title: incident.title, severity: incident.severity, impact: incident.impact || "", workaround: incident.workaround || "",
        responsible: incident.responsible_id || "", startedAt: toLocalInput(incident.started_at), detectedAt: toLocalInput(incident.detected_at) });
      req.setError("");
    }
  }, [open, incident]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!incident || !draft) return null;
  const problem = incidentFormProblem(draft);
  const body = { expected_version: incident.version };
  if (draft.title.trim() !== incident.title) body.title = draft.title.trim();
  if (draft.severity !== incident.severity) body.severity = draft.severity;
  if (draft.impact.trim() !== (incident.impact || "")) body.impact = draft.impact.trim();
  if (draft.workaround.trim() !== (incident.workaround || "")) body.workaround = draft.workaround.trim();
  if (draft.responsible !== (incident.responsible_id || "")) body.responsible_id = draft.responsible || null;
  if (fromLocalInput(draft.startedAt) !== new Date(incident.started_at).toISOString()) body.started_at = fromLocalInput(draft.startedAt);
  if (fromLocalInput(draft.detectedAt) !== new Date(incident.detected_at).toISOString()) body.detected_at = fromLocalInput(draft.detectedAt);
  const changed = Object.keys(body).length > 1;
  const submit = () => req.run(() => api.patch(`/product/incidents/${incident.id}`, body), onDone);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Modifier l'incident</DialogTitle>
          <DialogDescription>{incident.number} · l'état se change avec les boutons de la page.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="incident-edit-title">Titre *</Label>
            <Input id="incident-edit-title" value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} maxLength={140} data-testid="incident-edit-title" />
          </div>
          <SelectField id="incident-edit-severity" label="Gravité" value={draft.severity} onChange={(v) => setDraft((d) => ({ ...d, severity: v }))} options={INCIDENT_SEVERITY_LABELS} />
          <div className="space-y-1.5">
            <Label htmlFor="incident-edit-impact">Impact constaté</Label>
            <Textarea id="incident-edit-impact" value={draft.impact} onChange={(e) => setDraft((d) => ({ ...d, impact: e.target.value }))} rows={3} maxLength={2000} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="incident-edit-workaround">Contournement</Label>
            <Textarea id="incident-edit-workaround" value={draft.workaround} onChange={(e) => setDraft((d) => ({ ...d, workaround: e.target.value }))} rows={3} maxLength={2000}
              data-testid="incident-edit-workaround" />
          </div>
          <OwnerSelect id="incident-edit-responsible" label="Responsable" value={draft.responsible} onChange={(v) => setDraft((d) => ({ ...d, responsible: v }))} people={people} />
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="incident-edit-started">Début *</Label>
              <Input id="incident-edit-started" type="datetime-local" value={draft.startedAt} onChange={(e) => setDraft((d) => ({ ...d, startedAt: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="incident-edit-detected">Détecté</Label>
              <Input id="incident-edit-detected" type="datetime-local" value={draft.detectedAt} onChange={(e) => setDraft((d) => ({ ...d, detectedAt: e.target.value }))} />
            </div>
          </div>
          {problem && <p className="text-xs text-slate-500">{problem}</p>}
        </div>
        <Footer busy={req.busy} error={req.error} onCancel={() => onOpenChange(false)} onSubmit={submit} disabled={!!problem || !changed} label="Enregistrer" testid="incident-edit-submit" />
      </DialogContent>
    </Dialog>
  );
}

// --- A move of the state: a reason for a step back, the real resolution time, the post-mortem reminder for the closing -------------
export function IncidentStatusDialog({ incident, move, onOpenChange, onDone }) {
  const req = useRequest();
  const [reason, setReason] = useState("");
  const [resolvedAt, setResolvedAt] = useState("");
  useEffect(() => { if (move) { setReason(""); setResolvedAt(""); req.setError(""); } }, [move]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!incident || !move) return null;
  const label = incidentMoveLabel(move);
  const needsWorkaround = move.needs_workaround;
  const needsPostmortem = move.needs_postmortem;
  const valid = (!move.needs_reason || reason.trim().length >= 3) && !needsWorkaround && !needsPostmortem;
  const submit = () => {
    const body = { expected_version: incident.version, status: move.state };
    if (move.needs_reason) body.reason = reason.trim();
    if (move.state === "resolved" && resolvedAt) body.resolved_at = fromLocalInput(resolvedAt);
    req.run(() => api.post(`/product/incidents/${incident.id}/status`, body), onDone);
  };
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{label}</DialogTitle>
          <DialogDescription>{incident.number} · {INCIDENT_STATE_LABELS[incident.state]} → {INCIDENT_STATE_LABELS[move.state]}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {needsWorkaround && (
            <p className="text-sm text-amber-800" data-testid="incident-move-needs-workaround">
              Décrivez d'abord le contournement mis en place : « Modifier » dans le menu de la page.
            </p>
          )}
          {needsPostmortem && (
            <p className="text-sm text-amber-800" data-testid="incident-move-needs-postmortem">
              Un incident {incident.severity === "critical" ? "critique" : "majeur"} ne se clôt qu'avec son post-mortem complet (cause, impact constaté, enseignements).
            </p>
          )}
          {move.needs_reason && (
            <div className="space-y-1.5">
              <Label htmlFor="incident-move-reason">Motif *</Label>
              <Textarea id="incident-move-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500} data-testid="incident-move-reason"
                placeholder="Pourquoi revenir en arrière ?" />
            </div>
          )}
          {move.state === "resolved" && !move.backward && (
            <div className="space-y-1.5">
              <Label htmlFor="incident-move-resolved">Résolu le</Label>
              <Input id="incident-move-resolved" type="datetime-local" value={resolvedAt} onChange={(e) => setResolvedAt(e.target.value)} className="max-w-[16rem]" />
              <p className="text-xs text-slate-500">Laissez vide pour « maintenant » ; indiquez l'heure réelle si la correction est plus ancienne.</p>
            </div>
          )}
          {move.state === "closed" && !needsPostmortem && <p className="text-sm text-slate-600">Un incident clos ne se rouvre pas : s'il revient, on en ouvre un nouveau, lié à celui-ci.</p>}
        </div>
        <Footer busy={req.busy} error={req.error} onCancel={() => onOpenChange(false)} onSubmit={submit} disabled={!valid} label={label} testid="incident-move-submit" />
      </DialogContent>
    </Dialog>
  );
}

// --- Post-mortem: cause, impact constaté, enseignements ------------------------------------------------------------------------------
export function PostmortemDialog({ incident, open, onOpenChange, onDone }) {
  const req = useRequest();
  const [draft, setDraft] = useState({ cause: "", impact: "", lessons: "" });
  useEffect(() => {
    if (open && incident) {
      const pm = incident.postmortem || {};
      setDraft({ cause: pm.cause || "", impact: pm.impact || "", lessons: pm.lessons || "" });
      req.setError("");
    }
  }, [open, incident]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!incident) return null;
  const submit = () => req.run(() => api.put(`/product/incidents/${incident.id}/postmortem`, {
    expected_version: incident.version, cause: draft.cause.trim(), impact: draft.impact.trim(), lessons: draft.lessons.trim(),
  }), onDone);
  const filled = POSTMORTEM_FIELDS.filter((f) => draft[f.id].trim()).length;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Post-mortem</DialogTitle>
          <DialogDescription>{incident.number} · On cherche ce qui s'est passé et ce qu'on change, pas un coupable. Vous pouvez enregistrer en plusieurs fois.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {POSTMORTEM_FIELDS.map((f) => (
            <div key={f.id} className="space-y-1.5">
              <Label htmlFor={`postmortem-${f.id}`}>{f.label}</Label>
              <Textarea id={`postmortem-${f.id}`} value={draft[f.id]} onChange={(e) => setDraft((d) => ({ ...d, [f.id]: e.target.value }))} rows={3} maxLength={2000}
                data-testid={`postmortem-${f.id}`} />
              <p className="text-xs text-slate-500">{f.hint}</p>
            </div>
          ))}
        </div>
        <Footer busy={req.busy} error={req.error} onCancel={() => onOpenChange(false)} onSubmit={submit} disabled={filled === 0}
          label={filled === POSTMORTEM_FIELDS.length ? "Enregistrer le post-mortem" : "Enregistrer (incomplet)"} testid="postmortem-submit" />
      </DialogContent>
    </Dialog>
  );
}

// --- Link a ticket or the item that fixes it -----------------------------------------------------------------------------------------
export function IncidentTicketDialog({ incident, open, onOpenChange, onDone }) {
  const req = useRequest();
  useEffect(() => { if (open) req.setError(""); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!incident) return null;
  const pick = (ticket) => req.run(() => api.post(`/product/incidents/${incident.id}/tickets`, { ticket_id: ticket.id }), onDone);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rattacher un ticket</DialogTitle>
          <DialogDescription>Les tickets causés par l'incident. Seuls ceux que vous pouvez consulter sont proposés ; les pharmacies sont comptées, jamais nommées ici.</DialogDescription>
        </DialogHeader>
        <TicketPicker excludeIds={(incident.tickets || []).map((t) => t.id)} onPick={pick} testid="incident-ticket-picker" />
        {req.busy && <p className="text-xs text-slate-500">Rattachement…</p>}
        {req.error && <p className="text-sm text-red-600" role="alert" data-testid="product-dialog-error">{req.error}</p>}
      </DialogContent>
    </Dialog>
  );
}

export function IncidentItemDialog({ incident, open, onOpenChange, onDone }) {
  const req = useRequest();
  useEffect(() => { if (open) req.setError(""); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!incident) return null;
  const pick = (item) => req.run(() => api.post(`/product/incidents/${incident.id}/item`, { item_id: item.id }), onDone);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Lier le retour produit qui le corrige</DialogTitle>
          <DialogDescription>Le correctif suit son propre chemin (qualification, développement, version livrée).</DialogDescription>
        </DialogHeader>
        <ItemPicker onPick={pick} excludeIds={incident.item ? [incident.item.id] : []} testid="incident-item-picker" />
        {req.busy && <p className="text-xs text-slate-500">Liaison…</p>}
        {req.error && <p className="text-sm text-red-600" role="alert" data-testid="product-dialog-error">{req.error}</p>}
      </DialogContent>
    </Dialog>
  );
}
